import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.kiera.assistant');
import { OnDestroy, OnInit } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const ASSISTANT_RUNTIME_API_KEY_STORAGE = 'kiera.assistant.runtime_api_key';

type ChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    createdAt: number;
    rag?: {
        documents: number;
        matches: RagMatch[];
    } | null;
};

type RagMatch = {
    document_id: number;
    title: string;
    source_label: string;
    score: number;
};

type RagDocument = {
    id: number;
    title: string;
    original_name: string;
    source_path: string;
    source_type: string;
    priority: number;
    chunk_count: number;
    status: string;
    updated: string;
    exists: boolean;
    is_enabled: boolean;
    is_system: boolean;
};

type ManualSection = {
    title: string;
    tone: string;
    items: string[];
};

type ManualExample = {
    request: string;
    behavior: string;
};

type RagEditorState = {
    open: boolean;
    loading: boolean;
    saving: boolean;
    documentId: number;
    title: string;
    priority: number;
    enabled: boolean;
    content: string;
    contentEditable: boolean;
    supportsInlineEdit: boolean;
    contentMessage: string;
    sourceType: string;
    originalName: string;
    isSystem: boolean;
};

@Component({
 standalone: false,
 selector: 'wiz-page-kiera-assistant',
template: templateSource || '',
    styles: [`

/* file: /opt/app/project/main/.kiera-build/src/app/page.kiera.assistant/view.scss */
:host {
  display: flex;
  flex-direction: column;
  height: 100%;
}`],
})
export class PageKieraAssistantComponent implements OnInit, OnDestroy {
    public activeTab: 'chat' | 'settings' | 'rag' = 'chat';
    public config: any = {
        endpoint: '',
        model: '',
        api_key: '',
        api_key_masked: '',
        has_api_key: false,
        allowed_file_roots: [],
        system_prompt: '',
        temperature: 0.2,
    };
    public allowedRootsText: string = '';
    public allowedRoots: string[] = [];
    public allowedRootInput: string = '';
    public draft: string = '';
    public messages: ChatMessage[] = [];
    public errorMessage: string = '';
    public errorDetail: string = '';
    public saveMessage: string = '';
    public sending: boolean = false;
    public saving: boolean = false;
    public ragLoading: boolean = false;
    public ragSaving: boolean = false;
    public ragDeletingId: number = 0;
    public rootsSaving: boolean = false;
    public retryableError: boolean = false;
    public lastFailedDraft: string = '';
    public toolAccess: any = {
        enabled: false,
        role: 'viewer',
        allowed_file_roots: [],
        databases: [],
    };
    public manual: { overview: string; sections: ManualSection[]; examples: ManualExample[]; } = {
        overview: '',
        sections: [],
        examples: [],
    };
    public ragDocuments: RagDocument[] = [];
    public ragForm: { documentId: number; title: string; priority: number; enabled: boolean; isSystem: boolean; } = {
        documentId: 0,
        title: '',
        priority: 100,
        enabled: true,
        isSystem: false,
    };
    public ragSelectedFile: File | null = null;
    public ragSelectedFileName: string = '';
    public ragEditor: RagEditorState = this.createEmptyRagEditor();
    public suggestedPrompts: string[] = [
        'Summarize the current relay deployment state and next safe checks.',
        'How should I debug a relay node that powers off before relay-ready?',
        'Explain a safe VLAN auto-tagging rollout plan for PXE and diskless nodes.',
        'Outline a thousand-node relay design with subnet and fan-out guidance.',
    ];
    private langSub?: Subscription;
    private manualRaw: { overview: string; sections: ManualSection[]; examples: ManualExample[]; } | null = null;

    private readonly storageSyncHandler = (event: StorageEvent) => {
        if (event.key !== ASSISTANT_RUNTIME_API_KEY_STORAGE) {
            return;
        }
        const nextApiKey = this.readRuntimeApiKey();
        if (String(this.config.api_key || '') === nextApiKey) {
            return;
        }
        this.config = {
            ...this.config,
            api_key: nextApiKey,
            has_api_key: nextApiKey !== '',
            api_key_masked: '',
        };
        void this.service.render();
    };

    constructor(@Inject( Service) public service: Service,@Inject( TranslateService)  private translate: TranslateService) { }

    private createEmptyRagEditor(): RagEditorState {
        return {
            open: false,
            loading: false,
            saving: false,
            documentId: 0,
            title: '',
            priority: 100,
            enabled: true,
            content: '',
            contentEditable: false,
            supportsInlineEdit: false,
            contentMessage: '',
            sourceType: '',
            originalName: '',
            isSystem: false,
        };
    }

    private readRuntimeApiKey() {
        try {
            const localValue = globalThis.localStorage?.getItem(ASSISTANT_RUNTIME_API_KEY_STORAGE) || '';
            if (localValue !== '') {
                return localValue;
            }
            const legacySessionValue = globalThis.sessionStorage?.getItem(ASSISTANT_RUNTIME_API_KEY_STORAGE) || '';
            if (legacySessionValue !== '') {
                globalThis.localStorage?.setItem(ASSISTANT_RUNTIME_API_KEY_STORAGE, legacySessionValue);
                globalThis.sessionStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            }
            return legacySessionValue;
        } catch (error) {
            return '';
        }
    }

    private writeRuntimeApiKey(value: string) {
        try {
            const text = String(value || '');
            globalThis.sessionStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            if (text === '') {
                globalThis.localStorage?.removeItem(ASSISTANT_RUNTIME_API_KEY_STORAGE);
            } else {
                globalThis.localStorage?.setItem(ASSISTANT_RUNTIME_API_KEY_STORAGE, text);
            }
        } catch (error) {
            return;
        }
    }

    private clearErrorState() {
        this.errorMessage = '';
        this.errorDetail = '';
        this.retryableError = false;
    }

    private normalizeAllowedRoots(values: any[]) {
        const next: string[] = [];
        const seen = new Set<string>();
        const items = Array.isArray(values) ? values : [];
        for (const item of items) {
            const text = String(item || '').trim();
            if (text === '' || seen.has(text)) {
                continue;
            }
            seen.add(text);
            next.push(text);
        }
        return next;
    }

    private syncAllowedRootsState(values: any[]) {
        this.allowedRoots = this.normalizeAllowedRoots(values);
        this.allowedRootsText = this.allowedRoots.join('\n');
        this.config.allowed_file_roots = [...this.allowedRoots];
        this.toolAccess = {
            ...this.toolAccess,
            allowed_file_roots: [...this.allowedRoots],
        };
    }

    public ragDocumentsCountText() {
        return this.translate.instant('page.assistant.documents_count', {
            count: this.ragDocuments.length,
        });
    }

    public ragDocumentChunksUpdatedText(document: RagDocument) {
        return this.translate.instant('page.assistant.chunks_updated', {
            count: document?.chunk_count ?? 0,
            updated: document?.updated || '',
        });
    }

    public async ngOnInit() {
        await this.service.init();
        this.langSub = this.translate.onLangChange.subscribe(async () => {
            this.manual = this.localizeManual(this.manualRaw || this.manual);
            await this.service.render();
        });
        try {
            globalThis.addEventListener?.('storage', this.storageSyncHandler);
        } catch (error) {
            return;
        }
        await this.loadConfig();
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    public ngOnDestroy() {
        if (this.langSub) this.langSub.unsubscribe();
        try {
            globalThis.removeEventListener?.('storage', this.storageSyncHandler);
        } catch (error) {
            return;
        }
    }

    private applyConfigData(data: any) {
        const runtimeApiKey = this.readRuntimeApiKey();
        this.config = {
            endpoint: data.endpoint || '',
            model: data.model || '',
            api_key: runtimeApiKey,
            api_key_masked: '',
            has_api_key: runtimeApiKey !== '',
            allowed_file_roots: Array.isArray(data.allowed_file_roots) ? data.allowed_file_roots : [],
            system_prompt: data.system_prompt || '',
            temperature: typeof data.temperature === 'number' ? data.temperature : 0.2,
        };
        this.syncAllowedRootsState(this.config.allowed_file_roots || []);
    }

    public async loadConfig() {
        this.clearErrorState();
        const { code, data } = await wiz.call('config');
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to load assistant settings.';
            return;
        }

        this.applyConfigData(data || {});
        this.toolAccess = data.tool_access || this.toolAccess;
        this.manualRaw = data.manual || this.manualRaw;
        this.manual = this.localizeManual(this.manualRaw || this.manual);
    }

    public async loadRagDocuments(render: boolean = true) {
        this.ragLoading = true;
        if (render) {
            await this.service.render();
        }

        const { code, data } = await wiz.call('rag_list');
        this.ragLoading = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to load indexed documents.';
            if (render) {
                await this.service.render();
            }
            return;
        }

        this.ragDocuments = Array.isArray(data?.rows) ? data.rows : [];
        if (render) {
            await this.service.render();
        }
    }

    private isEnglishLanguage() {
        return String(this.translate.currentLang || this.translate.getDefaultLang() || 'ko').toLowerCase().startsWith('en');
    }

    private localizeManualText(value: string) {
        if (!this.isEnglishLanguage()) return value;

        const text = String(value ?? '');
        if (text.startsWith('현재 로그인 역할은 ') && text.endsWith(' 입니다.')) {
            return `Current signed-in role: ${text.slice('현재 로그인 역할은 '.length, -' 입니다.'.length)}.`;
        }

        const replacements: Array<[string, string]> = [
            ['KiERA Assistant는 읽기 작업은 바로 수행하지만, DB나 서버 파일을 바꾸는 작업은 대화에서 명시적으로 확인받은 뒤에만 실행합니다.', 'KiERA Assistant runs read-only work immediately, but it only executes database or server file changes after explicit confirmation in the conversation.'],
            ['읽기 작업', 'Read-only work'],
            ['확인 후 실행되는 변경 작업', 'Changes that run only after confirmation'],
            ['운영 용어 해석', 'Operational term handling'],
            ['현재 세션 제한', 'Current session limits'],
            ['서버 파일 목록 조회와 텍스트 파일 읽기', 'List server files and read text files'],
            ['구성된 데이터베이스와 테이블 목록 조회', 'List configured databases and tables'],
            ['SELECT, SHOW, DESCRIBE, EXPLAIN 같은 읽기 전용 SQL 실행', 'Run read-only SQL such as SELECT, SHOW, DESCRIBE, and EXPLAIN'],
            ['서버 파일 생성, 덮어쓰기, append', 'Create, overwrite, or append server files'],
            ['INSERT, UPDATE, DELETE, ALTER, CREATE, DROP 같은 DB 변경 SQL', 'Run database-changing SQL such as INSERT, UPDATE, DELETE, ALTER, CREATE, and DROP'],
            ['release, deploy, commission 같은 KiERA Actions 실행', 'Execute KiERA actions such as release, deploy, and commission'],
            ['이런 작업은 assistant가 바로 실행하지 않고 confirm 또는 cancel 답변을 기다립니다.', 'For these operations, the assistant does not execute immediately and waits for a confirm or cancel reply.'],
            ['release는 KiERA 작업 실행의 release action을 뜻하며 DB row 삭제를 뜻하지 않습니다.', 'release means the KiERA release action and does not mean deleting database rows.'],
            ['deploy는 KiERA deploy action을 뜻하며 상태 컬럼 강제 수정으로 대체하면 안 됩니다.', 'deploy means the KiERA deploy action and must not be approximated by forcing status column changes.'],
            ['commission 또는 commissioning은 KiERA 커미셔닝 작업을 뜻하며 임의 DB 변경이나 파일 수정으로 흉내내면 안 됩니다.', 'commission or commissioning means the KiERA commissioning operation and must not be approximated by arbitrary database or file changes.'],
            ['server tool access는 superadmin 세션에서만 활성화됩니다.', 'Server tool access is enabled only for superadmin sessions.'],
            ['현재 세션에서는 server tool access가 활성화되어 있습니다.', 'Server tool access is enabled in the current session.'],
            ['KiERA action tool access는 operator 이상 세션에서 활성화됩니다.', 'KiERA action tool access is enabled for operator-or-higher sessions.'],
            ['현재 세션에서는 KiERA action tool access가 활성화되어 있습니다.', 'KiERA action tool access is enabled in the current session.'],
            ['release, deploy, commission 같은 action 용어는 전용 action 실행 도구가 없으면 DB 또는 파일 변경으로 대체 실행하지 않습니다.', 'Action terms such as release, deploy, and commission are never approximated by database or file changes when dedicated action tools are unavailable.'],
            ['node 114 상태만 확인해줘', 'Check only the status of node 114'],
            ['읽기 작업이므로 바로 조회합니다.', 'This is read-only work, so it is checked immediately.'],
            ['node 114 release 해줘', 'Release node 114'],
            ['release를 action 의미로 해석하고, execute_kiera_action 확인 대기 상태를 만든 뒤 승인 시 Actions 경로로 실행합니다.', 'Interpret release as an action, create an execute_kiera_action confirmation wait state, and run through the Actions path after approval.'],
            ['113,114,115 노드에 ubuntu, centos, rocky 이미지를 순서대로 deploy 해줘', 'Deploy ubuntu, centos, and rocky images to nodes 113, 114, and 115 in order'],
            ['노드별 deploy plan을 만들고 이미지 후보가 명확할 때만 확인 대기 상태를 만듭니다.', 'Create a per-node deploy plan and wait for confirmation only when the image candidates are unambiguous.'],
            ['이 UPDATE SQL 실행해줘', 'Run this UPDATE SQL'],
            ['실행 전 반드시 대화에서 확인을 요청합니다.', 'It always asks for confirmation in the conversation before execution.'],
        ];

        let localized = text;
        for (const [source, target] of replacements) {
            localized = localized.split(source).join(target);
        }
        return localized;
    }

    private localizeManual(manual: { overview: string; sections: ManualSection[]; examples: ManualExample[]; }) {
        return {
            overview: this.localizeManualText(manual?.overview || ''),
            sections: Array.isArray(manual?.sections)
                ? manual.sections.map((section: ManualSection) => ({
                    ...section,
                    title: this.localizeManualText(section?.title || ''),
                    items: Array.isArray(section?.items) ? section.items.map((item: string) => this.localizeManualText(item)) : [],
                }))
                : [],
            examples: Array.isArray(manual?.examples)
                ? manual.examples.map((example: ManualExample) => ({
                    request: this.localizeManualText(example?.request || ''),
                    behavior: this.localizeManualText(example?.behavior || ''),
                }))
                : [],
        };
    }

    public async saveConfig() {
        this.clearErrorState();
        this.saveMessage = '';
        this.writeRuntimeApiKey(this.config.api_key || '');
        this.saving = true;
        await this.service.render();

        const { code, data } = await wiz.call('save_config', {
            endpoint: this.config.endpoint || '',
            model: this.config.model || '',
            allowed_file_roots: this.allowedRoots.join('\n'),
            system_prompt: this.config.system_prompt || '',
            temperature: String(this.config.temperature ?? 0.2),
        });

        this.saving = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to save assistant settings.';
            await this.service.render();
            return;
        }

        this.applyConfigData(data || {});
        this.toolAccess = {
            ...this.toolAccess,
            allowed_file_roots: Array.isArray(this.config.allowed_file_roots) ? this.config.allowed_file_roots : [],
        };
        this.saveMessage = 'Assistant settings saved.';
        await this.service.render();
    }

    public async clearConversation() {
        this.messages = [];
        this.draft = '';
        this.clearErrorState();
        this.saveMessage = '';
        this.lastFailedDraft = '';
        await this.service.render();
    }

    public async switchTab(tab: 'chat' | 'settings' | 'rag') {
        this.activeTab = tab;
        if (tab === 'rag') {
            await this.loadRagDocuments(false);
        }
        await this.service.render();
    }

    public async applyPrompt(prompt: string) {
        this.draft = prompt;
        await this.service.render();
    }

    public async onDraftKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            await this.send();
        }
    }

    public async send() {
        const text = (this.draft || '').trim();
        if (text === '' || this.sending) {
            return;
        }

        const history = this.messages.slice(-12).map((message) => ({ role: message.role, content: message.content }));

        this.messages = [
            ...this.messages,
            { role: 'user', content: text, createdAt: Date.now(), rag: null },
        ];
        this.draft = '';
        this.clearErrorState();
        this.saveMessage = '';
        this.sending = true;
        await this.service.render();

        const { code, data } = await wiz.call('request', {
            query: text,
            history: JSON.stringify(history),
            api_key: this.config.api_key || '',
            source: 'assistant-page',
            page_path: globalThis.location?.pathname || '/kiera/assistant',
        });

        this.sending = false;
        if (code !== 200) {
            this.errorMessage = data?.message || ((code === 429 || code === 503)
                ? this.translate.instant('page.assistant.api_overloaded')
                : 'Assistant request failed.');
            this.errorDetail = data?.detail || '';
            this.retryableError = Boolean(data?.retryable || code === 429 || code === 503);
            this.lastFailedDraft = text;
            await this.service.render();
            return;
        }

        this.lastFailedDraft = '';
        this.messages = [
            ...this.messages,
            {
                role: 'assistant',
                content: data?.reply || 'No response received.',
                createdAt: Date.now(),
                rag: data?.rag || null,
            },
        ];
        await this.service.render();
    }

    public onRagFileSelected(event: any) {
        const files = event?.target?.files;
        if (files && files.length > 0) {
            this.ragSelectedFile = files[0];
            this.ragSelectedFileName = this.ragSelectedFile?.name || '';
        } else {
            this.ragSelectedFile = null;
            this.ragSelectedFileName = '';
        }
        this.service.render();
    }

    public downloadRagSample() {
        globalThis.location.href = '/wiz/api/page.kiera.assistant/rag_sample_download';
    }

    public async saveRagDocument(fileInput?: any) {
        if (this.ragSaving) {
            return;
        }

        if (this.ragForm.isSystem && this.ragSelectedFile) {
            this.errorMessage = this.translate.instant('page.assistant.error_system_file_replace');
            await this.service.render();
            return;
        }

        if (!this.ragForm.documentId && !this.ragSelectedFile) {
            this.errorMessage = this.translate.instant('page.assistant.error_select_upload_file');
            await this.service.render();
            return;
        }

        this.clearErrorState();
        this.saveMessage = '';
        this.ragSaving = true;
        await this.service.render();

        const formData = new FormData();
        formData.append('title', this.ragForm.title || '');
        formData.append('priority', String(this.ragForm.priority ?? 100));
        formData.append('enabled', this.ragForm.enabled ? 'true' : 'false');
        if (this.ragForm.documentId) {
            formData.append('document_id', String(this.ragForm.documentId));
        }
        if (this.ragSelectedFile) {
            formData.append('file', this.ragSelectedFile);
        }

        let data: any = null;
        let code = 0;
        try {
            const response = await fetch('/wiz/api/page.kiera.assistant/rag_save', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            const result = await response.json();
            code = result?.code || 0;
            data = result?.data || result;
        } catch (error: any) {
            code = 0;
            data = { message: error?.message || 'Failed to upload document.' };
        }

        this.ragSaving = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to index document.';
            await this.service.render();
            return;
        }

        this.saveMessage = this.ragForm.documentId ? 'RAG document updated.' : 'RAG document uploaded.';
        this.ragForm = {
            documentId: 0,
            title: '',
            priority: 100,
            enabled: true,
            isSystem: false,
        };
        this.ragSelectedFile = null;
        this.ragSelectedFileName = '';
        if (fileInput) {
            fileInput.value = '';
        }
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    public async editRagDocument(document: RagDocument) {
        if (!document?.id) {
            return;
        }

        this.clearErrorState();
        this.saveMessage = '';
        this.ragEditor = {
            ...this.createEmptyRagEditor(),
            open: true,
            loading: true,
            documentId: document.id || 0,
            title: document.title || '',
            priority: Number(document.priority || 100),
            enabled: document.is_enabled !== false,
            sourceType: document.source_type || '',
            originalName: document.original_name || document.title || '',
            isSystem: document.is_system === true,
        };
        await this.service.render();

        const { code, data } = await wiz.call('rag_get', {
            document_id: String(document.id),
        });

        if (code !== 200) {
            this.ragEditor = this.createEmptyRagEditor();
            this.errorMessage = data?.message || 'Failed to load the RAG editor.';
            await this.service.render();
            return;
        }

        const payload = data?.data || data || {};
        this.ragEditor = {
            open: true,
            loading: false,
            saving: false,
            documentId: Number(payload.id || document.id || 0),
            title: payload.title || document.title || '',
            priority: Number(payload.priority || document.priority || 100),
            enabled: payload.is_enabled !== false,
            content: payload.content || '',
            contentEditable: payload.content_editable === true,
            supportsInlineEdit: payload.supports_inline_edit === true,
            contentMessage: payload.content_message || '',
            sourceType: payload.source_type || document.source_type || '',
            originalName: payload.original_name || document.original_name || document.title || '',
            isSystem: payload.is_system === true,
        };
        await this.service.render();
    }

    public async reindexRagDocument(document: RagDocument) {
        await this.editRagDocument(document);
    }

    public clearRagForm(fileInput?: any) {
        this.ragForm = {
            documentId: 0,
            title: '',
            priority: 100,
            enabled: true,
            isSystem: false,
        };
        this.ragSelectedFile = null;
        this.ragSelectedFileName = '';
        if (fileInput) {
            fileInput.value = '';
        }
        this.service.render();
    }

    public closeRagEditor() {
        this.ragEditor = this.createEmptyRagEditor();
        this.service.render();
    }

    public async saveRagEditor() {
        if (!this.ragEditor.open || this.ragEditor.loading || this.ragEditor.saving || !this.ragEditor.documentId) {
            return;
        }

        this.clearErrorState();
        this.saveMessage = '';
        this.ragEditor = {
            ...this.ragEditor,
            saving: true,
        };
        await this.service.render();

        const formData = new FormData();
        formData.append('document_id', String(this.ragEditor.documentId));
        formData.append('title', this.ragEditor.title || '');
        formData.append('priority', String(this.ragEditor.priority ?? 100));
        formData.append('enabled', this.ragEditor.enabled ? 'true' : 'false');
        if (this.ragEditor.contentEditable) {
            formData.append('content', this.ragEditor.content || '');
        }

        let data: any = null;
        let code = 0;
        try {
            const response = await fetch('/wiz/api/page.kiera.assistant/rag_save', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            const result = await response.json();
            code = result?.code || 0;
            data = result?.data || result;
        } catch (error: any) {
            code = 0;
            data = { message: error?.message || 'Failed to save the RAG document.' };
        }

        if (code !== 200) {
            this.ragEditor = {
                ...this.ragEditor,
                saving: false,
            };
            this.errorMessage = data?.message || 'Failed to save the RAG document.';
            await this.service.render();
            return;
        }

        this.saveMessage = 'RAG document updated.';
        this.ragEditor = this.createEmptyRagEditor();
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    private async persistAllowedRoots(nextRoots: string[], successMessage: string) {
        if (this.rootsSaving) {
            return false;
        }

        this.clearErrorState();
        this.saveMessage = '';
        this.rootsSaving = true;
        await this.service.render();

        const { code, data } = await wiz.call('save_allowed_roots', {
            allowed_file_roots: this.normalizeAllowedRoots(nextRoots).join('\n'),
        });

        this.rootsSaving = false;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to save AI access directories.';
            await this.service.render();
            return false;
        }

        this.syncAllowedRootsState(Array.isArray(data?.allowed_file_roots) ? data.allowed_file_roots : nextRoots);
        this.saveMessage = successMessage;
        await this.loadRagDocuments(false);
        await this.service.render();
        return true;
    }

    public async addAllowedRoot() {
        const candidate = String(this.allowedRootInput || '').trim();
        this.clearErrorState();
        this.saveMessage = '';

        if (candidate === '') {
            this.errorMessage = this.translate.instant('page.assistant.error_allowed_root_required');
            await this.service.render();
            return;
        }

        if (this.allowedRoots.includes(candidate)) {
            this.errorMessage = this.translate.instant('page.assistant.error_allowed_root_exists');
            await this.service.render();
            return;
        }

        const saved = await this.persistAllowedRoots([...this.allowedRoots, candidate], 'AI access directory added.');
        if (!saved) {
            return;
        }

        this.allowedRootInput = '';
        await this.service.render();
    }

    public async removeAllowedRoot(root: string) {
        await this.persistAllowedRoots(
            this.allowedRoots.filter((item) => item !== root),
            'AI access directory removed.'
        );
    }

    public async toggleRagDocument(document: RagDocument) {
        const enabled = document.is_enabled === false;
        const { code, data } = await wiz.call('rag_toggle', {
            document_id: String(document.id),
            enabled: enabled ? 'true' : 'false',
        });

        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to update document status.';
            await this.service.render();
            return;
        }

        this.saveMessage = enabled ? 'RAG document activated.' : 'RAG document deactivated.';
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    public async deleteRagDocument(document: RagDocument) {
        if (!document?.id || this.ragDeletingId === document.id) {
            return;
        }

        this.clearErrorState();
        this.saveMessage = '';
        this.ragDeletingId = document.id;
        await this.service.render();

        const { code, data } = await wiz.call('rag_delete', {
            document_id: String(document.id),
        });

        this.ragDeletingId = 0;
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to delete indexed document.';
            await this.service.render();
            return;
        }

        this.saveMessage = document.is_system ? 'Built-in rules document removed.' : 'RAG document removed.';
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    public hasBuiltinRagDocument() {
        return this.ragDocuments.some((document) => document.is_system === true);
    }

    public async restoreBuiltinRagDocument() {
        this.clearErrorState();
        this.saveMessage = '';

        const { code, data } = await wiz.call('rag_restore_builtin');
        if (code !== 200) {
            this.errorMessage = data?.message || 'Failed to restore built-in rules document.';
            await this.service.render();
            return;
        }

        this.saveMessage = 'Built-in rules document restored.';
        await this.loadRagDocuments(false);
        await this.service.render();
    }

    public async retryLastRequest() {
        if (!this.lastFailedDraft || this.sending) {
            return;
        }

        const lastMessage = this.messages[this.messages.length - 1];
        if (lastMessage && lastMessage.role === 'user' && lastMessage.content === this.lastFailedDraft) {
            this.messages = this.messages.slice(0, -1);
        }

        this.draft = this.lastFailedDraft;
        await this.send();
    }

    public isConfigReady() {
        return !!((this.config.endpoint || '').trim() && (this.config.model || '').trim());
    }

    public manualToneClass(tone: string) {
        if (tone === 'safe') return 'border-emerald-200 bg-emerald-50';
        if (tone === 'warning') return 'border-amber-200 bg-amber-50';
        if (tone === 'info') return 'border-sky-200 bg-sky-50';
        return 'border-zinc-200 bg-zinc-50';
    }
}
export default PageKieraAssistantComponent;
